import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Sparkles,
  Workflow,
  Wrench,
  ShieldCheck,
  GraduationCap,
  ArrowRight,
  Calendar,
  Quote,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  GalleryHorizontal,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICES = [
  { icon: Brain, label: "AI Strategy", blurb: "Where AI actually pays off for you" },
  { icon: Sparkles, label: "Prompt Engineering", blurb: "Prompts that work every time" },
  { icon: Workflow, label: "Workflow Automation", blurb: "Kill the busywork end to end" },
  { icon: Wrench, label: "Custom AI Tools", blurb: "Built for your exact process" },
  { icon: ShieldCheck, label: "Responsible AI & GenAI Security", blurb: "Ship safely, stay compliant" },
  { icon: GraduationCap, label: "AI Training", blurb: "Level up you and your team" },
];

const TESTIMONIALS = [
  {
    quote:
      "One session turned a vague 'we should use AI' idea into a two-week plan our team actually shipped.",
    name: "Operations lead",
    role: "Mid-size services firm",
  },
  {
    quote:
      "Kenny rewrote our prompts live on the call. Output quality jumped immediately — no more babysitting the model.",
    name: "Marketing manager",
    role: "B2B SaaS",
  },
  {
    quote:
      "We needed AI guardrails before rollout. Walked away with a clear, practical responsible-AI checklist.",
    name: "IT & compliance",
    role: "Regulated industry",
  },
];

const FAQS = [
  {
    q: "What's included in the $25 consult?",
    a: "A focused 1-on-1 working session with Kenneth Gordon. You describe your problem, and together you map the fastest practical path: tool choices, prompt or workflow fixes, and a prioritized set of next steps. You leave with concrete notes and recommendations you can act on right away.",
  },
  {
    q: "Who is this best for?",
    a: "Solo professionals, small teams, and business or nonprofit leaders who want real-world AI help without an agency retainer — plus students and career changers who want direction on AI skills.",
  },
  {
    q: "How long is a session?",
    a: "A single focused session scheduled at a time you pick during checkout. Come with one clear problem and we can go deep on it.",
  },
  {
    q: "What should I bring or prepare?",
    a: "A short description of your goal, any prompts, documents, or workflows you're already using, and the outcome you'd call a win. The more specific, the more you get out of it.",
  },
  {
    q: "Do you build the solution for me?",
    a: "The consult is advisory and hands-on guidance. If your project needs build work, we'll scope it at the end of the session as a separate engagement.",
  },
  {
    q: "What if I need to reschedule?",
    a: "You can cancel or rebook through the booking flow using the confirmation you receive after checkout.",
  },
];


const TESTIMONIAL_VIEW_KEY = "hire-ai-kenny-testimonial-view";

const TestimonialCard = ({ t }: { t: (typeof TESTIMONIALS)[number] }) => (
  <div className="h-full rounded-xl border border-border/60 bg-card/60 p-4 flex flex-col gap-3">
    <Quote className="h-4 w-4 text-primary" aria-hidden="true" />
    <p className="text-sm text-foreground/90">{t.quote}</p>
    <p className="text-xs text-muted-foreground mt-auto">
      <span className="font-semibold text-foreground">{t.name}</span> · {t.role}
    </p>
  </div>
);

const Testimonials = () => {
  const [view, setView] = useState<"grid" | "slider">("grid");
  const trackRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(TESTIMONIAL_VIEW_KEY);
      if (saved === "grid" || saved === "slider") setView(saved);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleView = useCallback(() => {
    setView((prev) => {
      const next = prev === "grid" ? "slider" : "grid";
      try {
        localStorage.setItem(TESTIMONIAL_VIEW_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const nudge = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          What people say
        </h3>
        <div className="flex items-center gap-1">
          {view === "slider" && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 focus-ring"
                onClick={() => nudge(-1)}
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 focus-ring"
                onClick={() => nudge(1)}
                aria-label="Next testimonial"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs focus-ring"
            onClick={toggleView}
            aria-pressed={view === "slider"}
            aria-label={view === "grid" ? "Switch testimonials to slider view" : "Switch testimonials to grid view"}
          >
            {view === "grid" ? (
              <GalleryHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {view === "grid" ? "Slider" : "Grid"}
          </Button>
        </div>
      </div>

      {view === "grid" ? (
        <ul className="grid gap-3 grid-cols-1 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <li key={t.name}>
              <TestimonialCard t={t} />
            </li>
          ))}
        </ul>
      ) : (
        <ul
          ref={trackRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-thin"
          tabIndex={0}
          aria-label="Testimonials, scrollable"
        >
          {TESTIMONIALS.map((t) => (
            <li key={t.name} className="snap-start shrink-0 w-[85%] sm:w-[48%] lg:w-[32%]">
              <TestimonialCard t={t} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface HireAIKennyProps {
  variant?: "full" | "compact";
}

export const HireAIKenny = ({ variant = "full" }: HireAIKennyProps) => {
  const compact = variant === "compact";

  const cta = (
    <Button asChild size={compact ? "default" : "lg"} className="w-full sm:w-auto gap-2 focus-ring">
      <Link to="/book-consultation">
        <Calendar className="h-4 w-4" aria-hidden="true" />
        Book AI Kenny — $25
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </Button>
  );

  const body = (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      <div className="flex flex-wrap items-center gap-3">
        <h2
          id="hire-ai-kenny-heading"
          className={compact ? "text-lg font-bold" : "text-2xl sm:text-3xl font-bold"}
        >
          Hire AI Kenny
        </h2>
        <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
          $25 · 1-on-1 session
        </Badge>
      </div>

      <p className={`text-muted-foreground ${compact ? "text-sm" : "text-base sm:text-lg max-w-2xl"}`}>
        Stuck on where to start with AI — or how to make it actually work? Book a focused, no-fluff
        session with Kenneth Gordon, the builder behind GenieBot. You bring the problem; you leave with a
        practical plan, working prompts, and next steps you can ship this week.
      </p>

      <ul
        className={`grid gap-3 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}
      >
        {SERVICES.map(({ icon: Icon, label, blurb }) => (
          <li
            key={label}
            className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 p-3"
          >
            <span className="p-1.5 rounded-lg bg-primary/15 shrink-0">
              <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">{label}</span>
              <span className="block text-xs text-muted-foreground">{blurb}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {cta}
        <span className="text-xs text-muted-foreground">
          Flat $25 per session. Pick a time that works — payment and scheduling handled at checkout.
        </span>
      </div>
    </div>
  );

  if (compact) {
    return (
      <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 to-background">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <CardContent className="relative p-5">{body}</CardContent>
      </Card>
    );
  }

  return (
    <section id="hire-ai-kenny" className="scroll-mt-24 py-16 sm:py-20 px-4" aria-labelledby="hire-ai-kenny-heading">
      <div className="max-w-5xl mx-auto space-y-6">
        <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="absolute top-0 right-0 w-56 h-56 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <CardContent className="relative p-6 sm:p-10">{body}</CardContent>
        </Card>

        <Testimonials />

        <Card className="border-border/60">
          <CardContent className="p-6 sm:p-8">
            <h3 className="text-lg sm:text-xl font-bold mb-4">Questions about the $25 consult</h3>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm sm:text-base">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};


export default HireAIKenny;
